import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { formatNumberDisplayByLang } from '@/helpers/formatters'
import { formatAllSignatureCurrency } from '@/helpers/currency'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import { axiosApi } from '@accor/customer-core'
import { API_LATAM_PLANS } from '@/constants/urls'
import { getLatamPlans } from '@/services/latam-service'
import { convertDateFromServerFormat } from '@/helpers/date-helpers'
import { logger } from '@/services/logger'
import {
  RRULE_TO_FREQUENCY,
  FREQUENCY_TYPE,
  allSignaturePlanName,
  PLAN_KEY_BY_CODE
} from '@/constants/subscriptions'
import { useRouter } from 'vue-router'
interface Plan {
  price: string
  statusPoints: string
  rewardPoints: string
  bonusRewardPoints4Months: string
  partnerTransfer: string
  pointsDoNotExpire: boolean
  quickAccessToElite: boolean
  exclusiveAccessDreamStay: boolean
  familyPointsTransfer: boolean
  statusAllGift: boolean
}

export interface Plans {
  essential: Plan
  discover: Plan
  explorer: Plan
  absolute: Plan
}

export interface labelAndDescription {
  label: string
  desc: string
}

export interface PlanHeader {
  name: string
  planName: string
  price: string
}

export interface LabelsAndDescriptions {
  statusPoints: labelAndDescription
  rewardPoints: labelAndDescription
  bonusRewardPoints4Months: labelAndDescription
  partnerTransfer: labelAndDescription
  pointsDoNotExpire: labelAndDescription
  quickAccessToElite: labelAndDescription
  exclusiveAccessDreamStay: labelAndDescription
  familyPointsTransfer: labelAndDescription
  statusAllGift: labelAndDescription
}

export const planNames: (keyof Plans)[] = [
  allSignaturePlanName.essential,
  allSignaturePlanName.discover,
  allSignaturePlanName.explorer,
  allSignaturePlanName.absolute
]

const getKeyByValue = (object: any, value: any) => {
  return Object.keys(object).find((key) => object[key] === value)
}

export const useAllSignatureChangePlan = async () => {
  const { t } = useI18n()
  const userStore = useUserStore()
  const { activeAllSignatureSubscription } = storeToRefs(userStore)
  const router = useRouter()

  const frequencyLabels: Record<FREQUENCY_TYPE, string> = {
    monthly: t('allsignature.frequency.perMonth'),
    quarterly: t('allsignature.frequency.perQuarter'),
    yearly: t('allsignature.frequency.perYear')
  }

  const currentPlan = computed(() => {
    const currentPlanCode = activeAllSignatureSubscription.value?.plan_code as
      | keyof typeof PLAN_KEY_BY_CODE
      | undefined
    return currentPlanCode ? PLAN_KEY_BY_CODE[currentPlanCode] : planNames[0]
  })

  const isUpgrade = (newPlan: keyof Plans | undefined) => {
    return planNames.indexOf(currentPlan.value) < planNames.indexOf(newPlan || planNames[0])
  }

  const currentRrule = computed(() => activeAllSignatureSubscription.value?.rrule)
  const currentFrequency = computed(() => {
    return currentRrule.value ? RRULE_TO_FREQUENCY[currentRrule.value] : ''
  })
  const currentPeriodEnds = convertDateFromServerFormat(
    activeAllSignatureSubscription.value?.current_period_ends
  )

  const latamPlans = await getLatamPlans().catch((err) => {
    logger.error(err)
  })

  const getPricingOption = async (planName: keyof Plans) => {
    const planCode = getKeyByValue(PLAN_KEY_BY_CODE, planName)
    if (!latamPlans) return undefined
    const chosenPlan = latamPlans?.items?.find((item) => item.plan_code === planCode)
    return chosenPlan?.pricing_options?.find((option) => option.rrule === currentRrule.value)
  }

  const getPriceToDisplay = async (planName: keyof Plans) => {
    const pricingOption = await getPricingOption(planName)
    const frequency = currentFrequency.value ? frequencyLabels[currentFrequency.value] : ''
    return `${formatAllSignatureCurrency({ amount: pricingOption?.amount, currency: pricingOption?.currency })} ${frequency}`
  }

  const changeSubscription = async (newPlan: keyof Plans) => {
    const isUpgradePlan = isUpgrade(newPlan)
    const changeType = isUpgradePlan ? 'immediately_charge_delta' : 'period_end'
    const pricingOption = await getPricingOption(newPlan)
    const subscriptionId = activeAllSignatureSubscription.value?.subscription_id
    const pricingOptionId = pricingOption?.pricing_option_id
    await axiosApi.put(`${API_LATAM_PLANS}/subscriptions/${subscriptionId}/pricing_option`, {
      pricing_option_id: pricingOptionId,
      change_type: changeType
    })

    setTimeout(() => {
      userStore.refreshUser()
    }, 3000)

    router.push({ name: 'subscription-card-details', params: { cardName: 'all-signature' } })
  }

  const perkNames: (keyof LabelsAndDescriptions)[] = [
    'statusPoints',
    'rewardPoints',
    'bonusRewardPoints4Months',
    'partnerTransfer',
    'pointsDoNotExpire',
    'quickAccessToElite',
    'exclusiveAccessDreamStay',
    'familyPointsTransfer',
    'statusAllGift'
  ]

  const headerLabels = {
    [allSignaturePlanName.essential]: t('allsignature.plan.p0.essential'),
    [allSignaturePlanName.discover]: t('allsignature.plan.p1.discover'),
    [allSignaturePlanName.explorer]: t('allsignature.plan.p2.explorer'),
    [allSignaturePlanName.absolute]: t('allsignature.plan.p3.absolute')
  }

  const labelsAndDescriptions: LabelsAndDescriptions = {
    statusPoints: {
      label: t('allsignature.benefits.label.0'),
      desc: t('allsignature.benefits.description.0')
    },
    rewardPoints: {
      label: t('allsignature.benefits.label.3'),
      desc: t('allsignature.benefits.description.3')
    },
    bonusRewardPoints4Months: {
      label: t('allsignature.benefits.label.4'),
      desc: t('allsignature.benefits.description.4')
    },
    partnerTransfer: {
      label: t('allsignature.benefits.label.5'),
      desc: t('allsignature.benefits.description.5')
    },
    pointsDoNotExpire: {
      label: t('allsignature.benefits.label.6'),
      desc: t('allsignature.benefits.description.6')
    },
    quickAccessToElite: {
      label: t('allsignature.benefits.label.7'),
      desc: t('allsignature.benefits.description.7')
    },
    exclusiveAccessDreamStay: {
      label: t('allsignature.benefits.label.8'),
      desc: t('allsignature.benefits.description.8')
    },
    familyPointsTransfer: {
      label: t('allsignature.benefits.label.1'),
      desc: t('allsignature.benefits.description.1')
    },
    statusAllGift: {
      label: t('allsignature.benefits.label.2'),
      desc: t('allsignature.benefits.description.2')
    }
  }

  const plans: Plans = {
    [allSignaturePlanName.essential]: {
      price: await getPriceToDisplay(allSignaturePlanName.essential),
      statusPoints: formatNumberDisplayByLang(t('allsignature.essential.statusPoints.amount')),
      rewardPoints: formatNumberDisplayByLang(t('allsignature.essential.rewardPoints.amount')),
      bonusRewardPoints4Months: formatNumberDisplayByLang(
        t('allsignature.essential.monthlyReward.amount')
      ),
      partnerTransfer: t('allsignature.essential.partnersTransfer.amount'),
      pointsDoNotExpire: true,
      quickAccessToElite: false,
      exclusiveAccessDreamStay: false,
      familyPointsTransfer: false,
      statusAllGift: false
    },
    [allSignaturePlanName.discover]: {
      price: await getPriceToDisplay(allSignaturePlanName.discover),
      statusPoints: formatNumberDisplayByLang(t('allsignature.discover.statusPoints.amount')),
      rewardPoints: formatNumberDisplayByLang(t('allsignature.discover.rewardPoints.amount')),
      bonusRewardPoints4Months: formatNumberDisplayByLang(
        t('allsignature.discover.monthlyReward.amount')
      ),
      partnerTransfer: t('allsignature.discover.partnersTransfer.amount'),
      pointsDoNotExpire: true,
      quickAccessToElite: true,
      exclusiveAccessDreamStay: true,
      familyPointsTransfer: false,
      statusAllGift: false
    },
    [allSignaturePlanName.explorer]: {
      price: await getPriceToDisplay(allSignaturePlanName.explorer),
      statusPoints: formatNumberDisplayByLang(t('allsignature.explorer.statusPoints.amount')),
      rewardPoints: formatNumberDisplayByLang(t('allsignature.explorer.rewardPoints.amount')),
      bonusRewardPoints4Months: formatNumberDisplayByLang(
        t('allsignature.explorer.monthlyReward.amount')
      ),
      partnerTransfer: t('allsignature.explorer.partnersTransfer.amount'),
      pointsDoNotExpire: true,
      quickAccessToElite: true,
      exclusiveAccessDreamStay: true,
      familyPointsTransfer: true,
      statusAllGift: false
    },
    [allSignaturePlanName.absolute]: {
      price: await getPriceToDisplay(allSignaturePlanName.absolute),
      statusPoints: formatNumberDisplayByLang(t('allsignature.absolute.statusPoints.amount')),
      rewardPoints: formatNumberDisplayByLang(t('allsignature.absolute.rewardPoints.amount')),
      bonusRewardPoints4Months: formatNumberDisplayByLang(
        t('allsignature.absolute.monthlyReward.amount')
      ),
      partnerTransfer: t('allsignature.absolute.partnersTransfer.amount'),
      pointsDoNotExpire: true,
      quickAccessToElite: true,
      exclusiveAccessDreamStay: true,
      familyPointsTransfer: true,
      statusAllGift: true
    }
  }

  const getValueByPerkAndPlan = (planName: keyof Plans, perkName: keyof LabelsAndDescriptions) =>
    plans[planName][perkName]

  const plansByPerksArray = perkNames.map((perkName) =>
    planNames.map((planName) => getValueByPerkAndPlan(planName, perkName))
  )

  const labelsAndDescriptionsArray = perkNames.map((perkName) => labelsAndDescriptions[perkName])

  const plansHeader = planNames.map(
    (planName): PlanHeader => ({
      name: planName,
      planName: headerLabels[planName],
      price: plans[planName].price
    })
  )

  const labelsAndDescriptionsByPlan = (planName: keyof Plans) =>
    perkNames.map((perkName) => ({
      ...labelsAndDescriptions[perkName],
      key: perkName,
      value: getValueByPerkAndPlan(planName, perkName)
    }))

  return {
    labelsAndDescriptions,
    labelsAndDescriptionsArray,
    plans,
    plansByPerksArray,
    plansHeader,
    perkNames,
    labelsAndDescriptionsByPlan,
    currentPlan,
    currentPeriodEnds,
    isUpgrade,
    changeSubscription,
    currentFrequency
  }
}
